
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/auth/useAuth';
import { useCartOperations } from '@/hooks/useCartOperations';
import { useCartDiscount } from './useCartDiscount';
import { useCartCalculations } from './cartCalculations';
import { useCartHandlers } from './cartHandlers';
import { useProductPricing } from '@/hooks/useProductPrices';
import { useTaxCalculation } from './useTaxCalculation';
import { useGiftPromotions } from '@/hooks/useGiftPromotions';
import { computeGiftLines, giftLinesTotal } from '@/utils/giftPromotions';
import {
  calculateCartTotalWithPricing,
  preloadShippingSettings,
  subscribeToShippingSettings
} from '@/utils/cartCalculations';


import { CartContext } from './CartContext';
import { CartContextType } from './types';

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const userId = user?.id || 'guest';
  
  const [previousUserId, setPreviousUserId] = useState<string>(userId);
  const [shippingZipCode, setShippingZipCode] = useState<string>('');
  const [, setShippingSettingsVersion] = useState(0);

  // Load admin shipping settings up-front and re-render once they arrive so the
  // cart never displays the hard-coded $5 / $30 defaults.
  useEffect(() => {
    const unsubscribe = subscribeToShippingSettings(() =>
      setShippingSettingsVersion((v) => v + 1)
    );
    preloadShippingSettings();
    return unsubscribe;
  }, []);

  
  const {
    cartItems,
    promoCode,
    appliedCredit,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    setPromoCode: setPromoCodeStorage,
    applyCredit: applyCreditStorage,
    resetAppliedCredit
  } = useCartOperations(userId, isAuthenticated, authLoading);
  
  const { getProductPrice, loading: pricesLoading } = useProductPricing();
  
  // Price sync logic - only run when prices finish loading
  useEffect(() => {
    if (pricesLoading || cartItems.length === 0) return;
    
    // Check if any cart items have outdated prices
    let hasStalePrice = false;

    cartItems.forEach(item => {
      const currentPrice = getProductPrice(item.id);
      if (currentPrice !== item.price) {
        hasStalePrice = true;
      }
    });

    // Only log in development if stale prices detected
    if (hasStalePrice && process.env.NODE_ENV === 'development') {
      console.log('[CART PRICE SYNC] Detected stale prices in cart');
    }
  }, [cartItems, getProductPrice, pricesLoading]);
  const { discount, promoEligibleItems, setDiscount, setPromoEligibleItems } = useCartDiscount(promoCode, cartItems);

  // Gift-with-purchase promotions (subtotal is paid items only)
  const { promotions: giftPromotions } = useGiftPromotions();
  const paidSubtotal = calculateCartTotalWithPricing(cartItems, getProductPrice);
  const giftLines = useMemo(
    () => (cartItems.length > 0 ? computeGiftLines(paidSubtotal, giftPromotions, getProductPrice) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paidSubtotal, giftPromotions, cartItems.length]
  );
  const giftTotal = giftLinesTotal(giftLines);
  // Calculate cart total and other amounts
  const {
    calculateCartTotal,
    calculateCartCount,
    calculateShippingCost,
    calculateDiscount,
    calculateCreditDiscount,
    calculateFinalTotal,
    calculateCartFinalTotal
  } = useCartCalculations(cartItems, discount, appliedCredit, getProductPrice, shippingZipCode, giftTotal);

  // Use the new tax calculation hook
  const cartTotal = calculateCartTotal();
  const { taxInfo, isLoading: taxLoading } = useTaxCalculation(
    cartTotal + giftTotal - discount, // Calculate tax on the discounted subtotal (gift charges included)
    0, // We've already applied the discount above
    shippingZipCode
  );
  
  // Calculate the final total with tax included
  const getFinalTotalWithTax = () => {
    const baseTotal = calculateFinalTotal();
    return baseTotal + taxInfo.taxAmount;
  };
  
  // Provide base total without tax for breakdown display
  const getBaseFinalTotal = () => {
    return calculateFinalTotal();
  };
  
  // Load shipping address from sessionStorage on mount
  useEffect(() => {
    const taxAddressStr = sessionStorage.getItem('taxAddress');
    if (taxAddressStr) {
      try {
        const address = JSON.parse(taxAddressStr);
        if (address.postal_code && address.postal_code !== shippingZipCode) {
          setShippingZipCode(address.postal_code);
        }
      } catch (e) {
        console.error('CartProvider: Error parsing shipping address:', e);
      }
    }
  }, []); // Only run once on mount
  
  const {
    handleApplyCredit,
    handleResetAppliedCredit,
    handleResetPromoCode,
    handleSetPromoCode
  } = useCartHandlers(
    appliedCredit,
    applyCreditStorage,
    resetAppliedCredit,
    setPromoCodeStorage,
    calculateCartTotal,
    calculateShippingCost,
    discount,
    setDiscount,
    setPromoEligibleItems
  );

  // User ID change tracking - only in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && previousUserId !== userId) {
      console.log('User ID changed in CartContext:', { previousUserId, currentUserId: userId });
    }
    setPreviousUserId(userId);
  }, [userId, previousUserId]);

  const contextValue: CartContextType = {
    cartItems, 
    addToCart, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    getCartTotal: calculateCartTotal,
    getCartCount: calculateCartCount,
    promoCode,
    setPromoCode: handleSetPromoCode,
    getDiscount: calculateDiscount,
    getShippingCost: calculateShippingCost,
    getFinalTotal: getFinalTotalWithTax,
    getBaseFinalTotal: getBaseFinalTotal,
    getCartFinalTotal: calculateCartFinalTotal,
    appliedCredit,
    applyCredit: handleApplyCredit,
    getCreditDiscount: calculateCreditDiscount,
    promoEligibleItems,
    resetAppliedCredit: handleResetAppliedCredit,
    resetPromoCode: handleResetPromoCode,
    // Tax-related functions
    setShippingZipCode,
    getTaxInfo: () => taxInfo,
    getTaxAmount: () => taxInfo.taxAmount,
    shippingZipCode,
    taxLoading,
    giftLines,
    getGiftTotal: () => giftTotal
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
